import { useAuthStore } from '@/lib/stores/authStore';
import type { SessionSummary } from '@/lib/types/consultation';
import type { DocumentSourceData } from '@/lib/api/medplum-resources';
import type { FhirSummaryPayload } from '@/lib/utils/caseSheetFhirPayload';
import {
  formatDiagnosisEntry,
  formatInvestigationEntry,
  formatRelationConditionEntry,
  formatRelationConditionList,
  isInvestigationItem,
  isRelationConditionItem,
} from '@/lib/utils/clinicalText';

const API_BASE_URL = import.meta.env.VITE_AUTH_API_URL;

function getAuthHeaders(): Record<string, string> {
  const token = useAuthStore.getState().token;
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function handleResponse<T>(response: Response, errorPrefix: string): Promise<T> {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `${errorPrefix}: ${response.status}`);
  }
  return response.json();
}

export interface CaseSheetSummaryData {
  encounterId: string;
  practitionerId: string;
  patientId?: string;
  patientSummary?: string;
  chiefComplaint?: string;
  clinicalSymptoms?: string;
  medicalHistory?: string;
  familyHistory?: string;
  generalExamination?: string;
  treatmentPlan?: string;
  doctorNotes?: string;
  medications?: unknown[];
  investigations?: unknown;
  assessmentDiagnosis?: string;
  socialHistory?: string;
  reviewOfSystems?: string;
  allergies?: string;
  followUpPlan?: string;
  pediatricMilestone?: string;
  adtOrder?: string;
  medicationHistory?: string;
  dietAdvice?: string;
  pranikCdss?: Record<string, unknown>;
  /** ICD-11 symptom codes extracted from history_of_present_illness.symptom_codes */
  diagnosisCodes?: Array<{ symptom: string; symptom_id: string }>;
}

export interface CaseSheetSummaryRecord extends CaseSheetSummaryData {
  id: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Extract a plain string from a SessionSummary field (handles { value, timestamp }
 * nested format). Array items are joined with '\n', not ', ' — each array element
 * is already the backend's own discrete bullet boundary, and a comma join would be
 * ambiguous whenever an item contains its own internal comma (e.g. "Stroke about
 * two years back, admitted for five days"), wrongly letting downstream bullet
 * splitters (splitProseToBullets/splitMedicalNarrativeToBullets, which already
 * treat '\n' as the authoritative boundary) break one item into two. A '\n' never
 * occurs naturally inside a single item's own text, so it round-trips losslessly.
 */
export function extractSessionField(field: unknown): string {
  if (!field) return '';
  if (typeof field === 'string') return field;
  if (Array.isArray(field)) {
    if (field.length > 0 && field.every(isRelationConditionItem)) {
      return formatRelationConditionList(field);
    }
    return field
      .map((item) => {
        if (typeof item === 'string') return item;
        if (typeof item === 'object' && item !== null && isRelationConditionItem(item)) {
          return formatRelationConditionEntry(item);
        }
        if (typeof item === 'object' && item !== null && isInvestigationItem(item)) {
          return formatInvestigationEntry(item);
        }
        if (typeof item === 'object' && item !== null && 'diagnosis' in item) {
          return formatDiagnosisEntry(item as { diagnosis: unknown; notes?: unknown });
        }
        if (typeof item === 'object' && item !== null && 'type' in item && 'value' in item) {
          const { type, value: val } = item as { type: string; value: string };
          return `${type}: ${val}`;
        }
        return String(item);
      })
      .join('\n');
  }
  if (typeof field === 'object' && 'value' in (field as object)) {
    const val = (field as { value?: unknown }).value;
    if (!val) return '';
    if (typeof val === 'string') return val;
    if (Array.isArray(val)) {
      if (val.length > 0 && val.every(isRelationConditionItem)) {
        return formatRelationConditionList(val);
      }
      return (val as unknown[])
        .map((item) => {
          if (typeof item === 'string') return item;
          if (typeof item === 'object' && item !== null && isRelationConditionItem(item)) {
            return formatRelationConditionEntry(item);
          }
          if (typeof item === 'object' && item !== null && isInvestigationItem(item)) {
            return formatInvestigationEntry(item);
          }
          if (typeof item === 'object' && item !== null && 'diagnosis' in item) {
            return formatDiagnosisEntry(item as { diagnosis: unknown; notes?: unknown });
          }
          if (typeof item === 'object' && item !== null && 'type' in item && 'value' in item) {
            const { type, value: val } = item as { type: string; value: string };
            return `${type}: ${val}`;
          }
          return String(item);
        })
        .join('\n');
    }
  }
  return '';
}

/** Build a DB save payload directly from a SessionSummary received from the AI agent.
 *  All data is available synchronously — no async queries needed. */
export function buildCaseSheetPayloadFromSession(
  encounterId: string,
  practitionerId: string,
  patientId: string,
  summary: SessionSummary
): CaseSheetSummaryData {
  const isNonEmpty = (field: unknown): boolean => {
    if (!field) return false;
    if (Array.isArray(field)) return field.length > 0;
    if (typeof field === 'object' && field !== null && 'value' in field) {
      const val = (field as { value: unknown }).value;
      return Array.isArray(val) ? val.length > 0 : !!val;
    }
    return true;
  };

  const getFirstField = (...keys: string[]): string => {
    for (const key of keys) {
      const value = extractSessionField((summary as unknown as Record<string, unknown>)[key]);
      if (value) return value;
    }
    return '';
  };
  // Medications: prefer generic `medications` alias, fall back to Ayurveda-native
  // `prescribed_medicines` (backend maps prescribed_medicines → medications).
  const medField = isNonEmpty(summary.medications)
    ? summary.medications
    : summary.prescribed_medicines;
  let medications: unknown[] = [];
  if (Array.isArray(medField)) {
    medications = medField;
  } else if (
    typeof medField === 'object' &&
    medField !== null &&
    'value' in medField &&
    Array.isArray((medField as { value: unknown }).value)
  ) {
    medications = (medField as { value: unknown[] }).value;
  }

  const followUpPlan = extractSessionField(summary.follow_up_plan) || undefined;

  return {
    encounterId,
    practitionerId,
    patientId,
    patientSummary: extractSessionField(summary.patient_summary) || undefined,
    chiefComplaint: extractSessionField(summary.chief_complaint) || undefined,
    // history_of_present_illness is the generic alias; fall back to `symptoms`.
    clinicalSymptoms:
      extractSessionField(summary.history_of_present_illness) ||
      extractSessionField(summary.symptoms) ||
      undefined,
    medicalHistory:
      [
        extractSessionField(summary.past_medical_surgical_history),
        extractSessionField(summary.medication_history),
        extractSessionField(summary.known_diseases_history),
      ]
        .filter(Boolean)
        .join('. ') || undefined,
    familyHistory: extractSessionField(summary.family_history) || undefined,
    generalExamination: extractSessionField(summary.physical_examination) || undefined,
    // treatment_plan_management is the generic alias; fall back to `treatment_plan`.
    treatmentPlan:
      extractSessionField(summary.treatment_plan_management) ||
      extractSessionField(summary.treatment_plan) ||
      undefined,
    doctorNotes: followUpPlan,
    medications,
    // investigations_orders is the generic alias; fall back to `relevant_investigations`.
    investigations: isNonEmpty(summary.investigations_orders)
      ? summary.investigations_orders
      : summary.relevant_investigations,
    // assessment_and_diagnosis is the generic alias; fall back to `assessment`.
    assessmentDiagnosis:
      extractSessionField(summary.assessment_and_diagnosis) ||
      extractSessionField(summary.assessment) ||
      undefined,
    socialHistory: extractSessionField(summary.social_history) || undefined,
    reviewOfSystems: extractSessionField(summary.review_of_systems) || undefined,
    allergies: extractSessionField(summary.allergies) || undefined,
    followUpPlan,
    medicationHistory: extractSessionField(summary.medication_history) || undefined,
    pediatricMilestone:
      getFirstField('pediatric_milestone', 'pediatric_milestones', 'pediatricMilestone') ||
      undefined,
    adtOrder: getFirstField('adt_order', 'adt_orders', 'adtOrder') || undefined,
    dietAdvice: getFirstField('diet_advice', 'meal_plan', 'dietAdvice') || undefined,
    pranikCdss: (summary.pranik_cdss ?? summary.ayurveda_cdss) as
      | Record<string, unknown>
      | undefined,
  };
}

/** Payload sent to POST /v2/api/case-sheet-summaries/fhir */
export interface SaveSummaryToFhirData {
  patientId: string;
  practitionerId: string;
  encounterId: string;
  /** Built by `buildFhirSummaryPayload` — the AI summary with the doctor's Case Sheet edits applied. */
  payload: FhirSummaryPayload;
}

export const caseSheetSummaryApi = {
  /**
   * Save the full AI summary as a FHIR transaction bundle in Medplum.
   * Safe to call multiple times for the same encounter — the backend will
   * upsert existing resources rather than create duplicates.
   */
  saveSummaryToFhir: async (data: SaveSummaryToFhirData): Promise<void> => {
    const response = await fetch(`${API_BASE_URL}/v2/api/case-sheet-summaries/fhir`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    await handleResponse(response, 'Failed to save summary to FHIR');
  },

  saveSummary: async (
    data: CaseSheetSummaryData,
    options?: { keepalive?: boolean }
  ): Promise<{
    success: boolean;
    summary: { id: string; encounterId: string; updatedAt: string };
  }> => {
    const response = await fetch(`${API_BASE_URL}/v2/api/case-sheet-summaries`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
      keepalive: options?.keepalive,
    });

    return handleResponse(response, 'Failed to save case sheet summary');
  },

  getSummary: async (encounterId: string): Promise<CaseSheetSummaryRecord | null> => {
    const response = await fetch(
      `${API_BASE_URL}/v2/api/case-sheet-summaries/${encodeURIComponent(encounterId)}`,
      {
        method: 'GET',
        headers: getAuthHeaders(),
      }
    );

    if (response.status === 404) {
      return null;
    }

    const data = await handleResponse<{ summary: CaseSheetSummaryRecord }>(
      response,
      'Failed to fetch case sheet summary'
    );
    return data.summary;
  },

  deleteSummary: async (encounterId: string): Promise<void> => {
    const response = await fetch(
      `${API_BASE_URL}/v2/api/case-sheet-summaries/${encodeURIComponent(encounterId)}`,
      {
        method: 'DELETE',
        headers: getAuthHeaders(),
      }
    );

    if (response.status === 404) {
      return;
    }

    await handleResponse(response, 'Failed to delete case sheet summary');
  },

  /**
   * Fetch the FHIR summary for an encounter from the backend API.
   * Returns the full summary data (same shape as DocumentSourceData).
   */
  getFhirSummary: async (encounterId: string): Promise<DocumentSourceData | null> => {
    const response = await fetch(
      `${API_BASE_URL}/v2/api/case-sheet-summaries/raw/${encodeURIComponent(encounterId)}`,
      {
        method: 'GET',
        headers: getAuthHeaders(),
      }
    );

    if (response.status === 404) {
      return null;
    }

    const data = await handleResponse<{
      success: boolean;
      encounterId: string;
      summary: DocumentSourceData;
    }>(response, 'Failed to fetch FHIR summary');
    return data.summary;
  },
};
